import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import config from '../config.js'

export const useLeadStore = defineStore('lead', () => {
  // State
  const pendingLeads = ref([])
  const isOnline = ref(navigator.onLine)
  const isSyncing = ref(false)

  // Constants for storage limits
  const MAX_PENDING_LEADS = 100; // Maximum pending leads to store
  const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit

  // Getters
  const pendingCount = computed(() => pendingLeads.value.length)
  const hasPendingLeads = computed(() => pendingLeads.value.length > 0)

  // Actions
  const loadPendingLeads = () => {
    try {
      const stored = localStorage.getItem('reepo_pending_leads')
      if (stored) {
        pendingLeads.value = JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error loading pending leads:', error)
      pendingLeads.value = []
    }
  }

  const savePendingLeads = () => {
    try {
      localStorage.setItem('reepo_pending_leads', JSON.stringify(pendingLeads.value))
    } catch (error) {
      console.error('Error saving pending leads:', error)
    }
  }

  const addToPendingLeads = (leadData, clientSlug, showSlug) => {
    // Check storage size
    const currentSize = new Blob([JSON.stringify(pendingLeads.value)]).size;
    if (currentSize > MAX_STORAGE_SIZE) {
      console.warn('Storage limit reached, removing oldest pending leads');
      pendingLeads.value = pendingLeads.value.slice(-50); // Keep only 50 most recent
    }
    
    // Check count limit
    if (pendingLeads.value.length >= MAX_PENDING_LEADS) {
      console.warn('Pending leads limit reached, removing oldest');
      pendingLeads.value = pendingLeads.value.slice(1);
    }
    
    const pendingLead = {
      id: Date.now() + Math.random(),
      data: leadData,
      clientSlug,
      showSlug,
      timestamp: new Date().toISOString()
    }
    
    pendingLeads.value.push(pendingLead)
    savePendingLeads()
  }

  const removeFromPendingLeads = (leadId) => {
    pendingLeads.value = pendingLeads.value.filter(lead => lead.id !== leadId)
    savePendingLeads()
  }

  const submitLeadToAPI = async (leadData, clientSlug, showSlug) => {
    const url = `${config.apiBaseUrl}/leads?client_slug=${clientSlug}&show_slug=${showSlug}`
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(leadData)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  const submitLead = async (leadData, clientSlug, showSlug) => {
    // If offline, add to pending queue
    if (!isOnline.value) {
      addToPendingLeads(leadData, clientSlug, showSlug)
      return { message: 'Lead saved offline. Will sync when connection is restored.' }
    }

    // If online, submit directly
    try {
      const result = await submitLeadToAPI(leadData, clientSlug, showSlug)
      return result
    } catch (error) {
      // If API call fails, add to pending queue
      addToPendingLeads(leadData, clientSlug, showSlug)
      throw error
    }
  }

  const syncPendingLeads = async () => {
    if (!isOnline.value || pendingLeads.value.length === 0 || isSyncing.value) {
      return
    }

    isSyncing.value = true

    try {
      const leadsToSync = [...pendingLeads.value]
      
      for (const pendingLead of leadsToSync) {
        try {
          await submitLeadToAPI(
            pendingLead.data, 
            pendingLead.clientSlug, 
            pendingLead.showSlug
          )
          
          // Remove from pending if successful
          removeFromPendingLeads(pendingLead.id)
        } catch (error) {
          console.error('Failed to sync lead:', pendingLead.id, error)
          // Keep in pending queue if sync fails
        }
      }
    } finally {
      isSyncing.value = false
    }
  }

  const updateOnlineStatus = (online) => {
    isOnline.value = online
    
    // If coming back online, try to sync pending leads
    if (online && hasPendingLeads.value) {
      setTimeout(() => {
        syncPendingLeads()
      }, 1000) // Small delay to ensure connection is stable
    }
  }

  // Initialize
  loadPendingLeads()

  // Listen for online/offline events
  window.addEventListener('online', () => updateOnlineStatus(true))
  window.addEventListener('offline', () => updateOnlineStatus(false))

  return {
    // State
    pendingLeads,
    isOnline,
    isSyncing,
    
    // Getters
    pendingCount,
    hasPendingLeads,
    
    // Actions
    submitLead,
    syncPendingLeads,
    updateOnlineStatus,
    loadPendingLeads,
    savePendingLeads
  }
}) 